// 경량 XML 파서 — 외부 의존성 없이 HWPX XML을 파싱
// DOMParser 없는 환경(Node.js)에서도 동작

export interface XmlNode {
  tag: string;
  attrs: Record<string, string>;
  children: XmlNode[];
  text: string;
}

/** XML 문자열을 XmlNode 트리로 파싱 */
export function parseXml(xml: string): XmlNode {
  const parser = new XmlParser(xml);
  return parser.parse();
}

/** 특정 태그명의 자식 노드 찾기 (네임스페이스 접두사 무시 가능) */
export function findChild(node: XmlNode, tag: string): XmlNode | undefined {
  return node.children.find((c) => c.tag === tag || stripNs(c.tag) === tag);
}

/** 특정 태그명의 모든 자식 노드 찾기 */
export function findChildren(node: XmlNode, tag: string): XmlNode[] {
  return node.children.filter((c) => c.tag === tag || stripNs(c.tag) === tag);
}

/** 네임스페이스 접두사 제거 (hh:head → head) */
export function stripNs(tag: string): string {
  const idx = tag.indexOf(':');
  return idx >= 0 ? tag.substring(idx + 1) : tag;
}

/** 속성값을 숫자로 파싱 (없으면 기본값) */
export function attrInt(node: XmlNode, name: string, defaultValue = 0): number {
  const v = node.attrs[name];
  if (v === undefined) return defaultValue;
  const n = parseInt(v, 10);
  return isNaN(n) ? defaultValue : n;
}

/** 속성값을 문자열로 (없으면 기본값) */
export function attrStr(node: XmlNode, name: string, defaultValue = ''): string {
  return node.attrs[name] ?? defaultValue;
}

/** 속성값을 boolean으로 ('1', 'true' → true) */
export function attrBool(node: XmlNode, name: string, defaultValue = false): boolean {
  const v = node.attrs[name];
  if (v === undefined) return defaultValue;
  return v === '1' || v === 'true';
}

// ─── XML Parser Implementation ───

class XmlParser {
  private xml: string;
  private pos: number;

  constructor(xml: string) {
    this.xml = xml;
    this.pos = 0;
  }

  parse(): XmlNode {
    this.skipWhitespace();
    // XML declaration 건너뛰기
    if (this.xml.startsWith('<?', this.pos)) {
      const end = this.xml.indexOf('?>', this.pos);
      if (end >= 0) this.pos = end + 2;
    }
    this.skipWhitespace();
    return this.parseElement();
  }

  private parseElement(): XmlNode {
    this.expect('<');
    const tag = this.readTagName();
    const attrs = this.readAttributes();

    this.skipWhitespace();

    // Self-closing tag
    if (this.xml.startsWith('/>', this.pos)) {
      this.pos += 2;
      return { tag, attrs, children: [], text: '' };
    }

    this.expect('>');

    // Children and text content
    const children: XmlNode[] = [];
    let text = '';
    let hasChildElements = false;

    while (this.pos < this.xml.length) {
      if (this.xml.startsWith('</', this.pos)) {
        // Closing tag
        this.pos += 2;
        const closingTag = this.readTagName();
        this.skipWhitespace();
        this.expect('>');
        // 자식 요소가 있으면 formatting 공백 trim, 텍스트 전용이면 보존
        const finalText = hasChildElements ? text.trim() : text;
        return { tag, attrs, children, text: finalText };
      }

      if (this.xml.startsWith('<!--', this.pos)) {
        // Comment — skip
        const end = this.xml.indexOf('-->', this.pos);
        if (end >= 0) this.pos = end + 3;
        continue;
      }

      if (this.xml.startsWith('<![CDATA[', this.pos)) {
        const end = this.xml.indexOf(']]>', this.pos);
        if (end >= 0) {
          text += this.xml.substring(this.pos + 9, end);
          this.pos = end + 3;
        }
        continue;
      }

      if (this.xml[this.pos] === '<') {
        hasChildElements = true;
        children.push(this.parseElement());
      } else {
        // Text content
        const nextTag = this.xml.indexOf('<', this.pos);
        if (nextTag >= 0) {
          text += this.xml.substring(this.pos, nextTag);
          this.pos = nextTag;
        } else {
          text += this.xml.substring(this.pos);
          this.pos = this.xml.length;
        }
      }
    }

    const finalText = hasChildElements ? text.trim() : text;
    return { tag, attrs, children, text: finalText };
  }

  private readTagName(): string {
    const start = this.pos;
    while (this.pos < this.xml.length) {
      const ch = this.xml[this.pos];
      if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '>' || ch === '/') break;
      this.pos++;
    }
    return this.xml.substring(start, this.pos);
  }

  private readAttributes(): Record<string, string> {
    const attrs: Record<string, string> = {};

    while (this.pos < this.xml.length) {
      this.skipWhitespace();
      const ch = this.xml[this.pos];
      if (ch === '>' || ch === '/') break;

      const name = this.readAttrName();
      this.skipWhitespace();
      this.expect('=');
      this.skipWhitespace();
      const value = this.readAttrValue();
      attrs[name] = this.unescapeXml(value);
    }

    return attrs;
  }

  private readAttrName(): string {
    const start = this.pos;
    while (this.pos < this.xml.length) {
      const ch = this.xml[this.pos];
      if (ch === '=' || ch === ' ' || ch === '\t' || ch === '\n' || ch === '>' || ch === '/') break;
      this.pos++;
    }
    return this.xml.substring(start, this.pos);
  }

  private readAttrValue(): string {
    const quote = this.xml[this.pos];
    if (quote !== '"' && quote !== "'") {
      throw new Error(`Expected quote at pos ${this.pos}`);
    }
    this.pos++;
    const start = this.pos;
    const end = this.xml.indexOf(quote, this.pos);
    if (end < 0) throw new Error(`Unterminated attribute value at pos ${start}`);
    this.pos = end + 1;
    return this.xml.substring(start, end);
  }

  private skipWhitespace(): void {
    while (this.pos < this.xml.length) {
      const ch = this.xml[this.pos];
      if (ch !== ' ' && ch !== '\t' && ch !== '\n' && ch !== '\r') break;
      this.pos++;
    }
  }

  private expect(ch: string): void {
    if (!this.xml.startsWith(ch, this.pos)) {
      throw new Error(
        `Expected "${ch}" at pos ${this.pos}, got "${this.xml.substring(this.pos, this.pos + 10)}"`,
      );
    }
    this.pos += ch.length;
  }

  private unescapeXml(str: string): string {
    return str
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'");
  }
}
